/**
 * Native capabilities exposed to the slices via the runtime bridge.
 * Each call reuses the admin user JWT so every call is scoped to those
 * permissions.
 */

import { MastraClient } from '@mastra/client-js';

import { fetchWithCurrentSessionJwt } from '../../auth/fetchWithCurrentSessionJwt';
import { LLM_SERVICE_BASE_URL } from '../../config/config';
import { fetcher } from '../../http/Fetcher';
import {
    CAPABILITY_ENDPOINTS,
    toCapabilityRequest,
} from './capabilityEndpoints';
import { downloadDocument, pickAndReadDocument } from './documentIO';
import { getCurrentGroveLocation, goToInternalPath } from './navigation';

/**
 * The bridge awaits whatever comes back, so a capability may resolve
 * synchronously — `navigation.*` deliberately does, to stay inside the user
 * activation that lets `window.open` through the popup blocker.
 */
export type CapabilityResolver = (params?: unknown) => unknown;

/** Mastra registry key for the fruit-slice LLM agent (its key in `agents`). */
const FRUIT_SLICE_AGENT_KEY = 'FruitSliceAgent';

/** Pull a required string param as a raw (unencoded) value. */
function reqRaw(params: unknown, key: string): string {
    const value = (params as Record<string, unknown> | undefined)?.[key];
    if (typeof value !== 'string' || value === '') {
        throw new Error(`missing required param: ${key}`);
    }
    return value;
}

/** Pull an optional string param (returns undefined if absent/blank). */
function optStr(params: unknown, key: string): string | undefined {
    const value = (params as Record<string, unknown> | undefined)?.[key];
    return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Pull an optional boolean param, defaulting to false. */
function optBool(params: unknown, key: string): boolean {
    return (params as Record<string, unknown> | undefined)?.[key] === true;
}

const API_CAPABILITIES: Record<string, CapabilityResolver> = Object.fromEntries(
    Object.entries(CAPABILITY_ENDPOINTS).map(
        ([capability, resolveEndpoint]) => [
            capability,
            async (params?: unknown) => {
                const request = toCapabilityRequest(resolveEndpoint(params));
                return await fetcher({
                    endpoint: request.path,
                    method: request.method,
                    body:
                        request.body === undefined
                            ? undefined
                            : JSON.stringify(request.body),
                });
            },
        ],
    ),
);

export const CAPABILITIES: Record<string, CapabilityResolver> = {
    ...API_CAPABILITIES,

    /**
     * Prompt an LLM and get its text response. Calls the native Mastra agent
     * `generate` endpoint in the llm-service as the signed-in admin. Params:
     * `{ prompt, system? }`; returns `{ text }`.
     */
    'llm.prompt': async (p) => {
        const prompt = reqRaw(p, 'prompt');
        const system = optStr(p, 'system');

        const messages = [
            ...(system != null
                ? [{ role: 'system' as const, content: system }]
                : []),
            { role: 'user' as const, content: prompt },
        ];

        const client = new MastraClient({
            baseUrl: LLM_SERVICE_BASE_URL,
            headers: {
                'Content-Type': 'application/json',
            },
            fetch: fetchWithCurrentSessionJwt,
        });

        const result = await client
            .getAgent(FRUIT_SLICE_AGENT_KEY)
            .generate(messages);

        return { text: result.text };
    },

    /**
     * Open a native file picker and hand the chosen file's bytes to the slice.
     * Entirely client-side — nothing is persisted. Params: `{ accept? }`
     * (a file-input accept string, e.g. 'image/*,.pdf'). Returns the file as
     * `{ name, contentType, size, dataBase64 }`, or `null` if the user cancels.
     */
    'document.upload': async (p) =>
        await pickAndReadDocument(optStr(p, 'accept')),

    /**
     * Compose a document in the slice and trigger a browser download. Entirely
     * client-side — nothing is persisted. Params:
     * `{ name, dataBase64, contentType? }`. Returns `true` once the download
     * has been triggered.
     */
    'document.download': async (p) => {
        await downloadDocument({
            name: reqRaw(p, 'name'),
            dataBase64: reqRaw(p, 'dataBase64'),
            contentType: optStr(p, 'contentType'),
        });
        return true;
    },

    'navigation.go': (p) => {
        goToInternalPath(reqRaw(p, 'path'), optBool(p, 'newTab'));
        return true;
    },

    'navigation.current': () => getCurrentGroveLocation(),
};
